import { makeAutoObservable, runInAction } from "mobx";
import { usageMeter } from "@/api";
import { chatSession, SessionMessage } from "@/aawebsocket/chatSession";
import { appState } from "./appState";

export type UsageBlockReason = "no_payment_method" | "no_ceiling" | "ceiling_reached" | "past_due";

/** This month's spending against the ceiling, in DKK excluding VAT (backend `meter_snapshot`). */
export type UsageMeter = {
    currency: string;
    period_start: string;
    period_end: string;
    days_left: number;
    has_account: boolean;
    demo_access: boolean;
    billing_organisation_id: number | null;
    spent: number;
    ceiling: number | null;
    remaining: number | null;
    recent_per_day: number;
    sustainable_per_day: number | null;
    /** recent_per_day / sustainable_per_day: above 1 the ceiling is reached before the month ends. */
    pace: number | null;
    projected_month_end: number;
    blocked_reason: UsageBlockReason | null;
    can_edit_ceiling?: boolean;
};

export type PaceZone = "cruise" | "brisk" | "fast";

export const PACE_BRISK = 0.9;
export const PACE_FAST = 1.2;

export const paceZone = (pace: number | null): PaceZone =>
    pace === null || pace < PACE_BRISK ? "cruise" : pace <= PACE_FAST ? "brisk" : "fast";

export const formatKroner = (amount: number, fractionDigits = 0) =>
    amount.toLocaleString(appState.locale, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });

/** Small amounts (one message) need øre to be meaningful. */
export const formatSmallKroner = (amount: number) => formatKroner(amount, amount < 10 ? 2 : 0);

export const isUsageBlockReason = (value: unknown): value is UsageBlockReason =>
    value === "no_payment_method" || value === "no_ceiling" || value === "ceiling_reached" || value === "past_due";

/** The usage block reason in an API error body (`{detail: {code: "usage_blocked", reason}}`), if any. */
export const usageBlockFromError = (error: any): UsageBlockReason | null => {
    const detail = error?.detail ?? error;
    return detail?.code === "usage_blocked" && isUsageBlockReason(detail.reason) ? detail.reason : null;
};

/**
 * The organisation's fuel tank for the speedometer. Fed by the REST meter (on
 * mount, focus and after uploads or summaries, since colleagues spend from the
 * same tank) and by the `usage_meter` event after every chat call.
 */
class UsageMeterState {
    meter: UsageMeter | null = null;
    /** Billed cost of the latest chat call, in DKK. */
    lastCycleCost: number | null = null;
    /** A refusal the server sent after the last meter reading. */
    refusedReason: UsageBlockReason | null = null;
    private requestSeq = 0;

    constructor() {
        makeAutoObservable(this);
        chatSession.addListener(this.onMessage);
    }

    onMessage = (message: SessionMessage) => {
        if (message.type !== "run_event") return;
        const event = message.event;
        if (event.type === "usage_meter") {
            const { cycle_cost, conversation_id, ...meter } = event.detail;
            this.apply(meter);
            this.lastCycleCost = cycle_cost;
        } else if (event.type === "usage_blocked") {
            this.block(event.detail.reason);
            void this.refresh();
        }
    };

    get blockedReason(): UsageBlockReason | null {
        return this.refusedReason ?? this.meter?.blocked_reason ?? null;
    }

    /** Demo organisations and super admins without a tank have nothing to show. */
    get metered(): boolean {
        return !!this.meter && !this.meter.demo_access && this.meter.has_account && this.meter.ceiling !== null;
    }

    get zone(): PaceZone {
        return paceZone(this.meter?.pace ?? null);
    }

    /** Share of the ceiling still left, 0 to 1. */
    get fuel(): number {
        const meter = this.meter;
        if (!meter || !meter.ceiling) return 0;
        return Math.max(0, Math.min(1, (meter.remaining ?? 0) / meter.ceiling));
    }

    apply = (meter: Omit<UsageMeter, "can_edit_ceiling"> & { can_edit_ceiling?: boolean }) => {
        this.meter = { ...meter, can_edit_ceiling: meter.can_edit_ceiling ?? this.meter?.can_edit_ceiling };
        this.refusedReason = null;
    };

    block = (reason: UsageBlockReason) => {
        this.refusedReason = reason;
    };

    clear = () => {
        this.requestSeq++;
        this.meter = null;
        this.lastCycleCost = null;
        this.refusedReason = null;
    };

    refresh = async () => {
        const seq = ++this.requestSeq;
        try {
            const res = await usageMeter();
            if (seq !== this.requestSeq || !res.data) return;
            runInAction(() => this.apply(res.data as UsageMeter));
        } catch (error) {
            console.warn("usage meter refresh failed", error);
        }
    };
}

export const usageMeterState = new UsageMeterState();
